// SEQ-180 phase 2: decoding `pnl_metric_registry` rows into the editor's
// metric options. Pure (no React, no supabase import) so it can be proven
// under Deno; usePnlMetricRegistry.ts is the thin hook around it.
//
// Code review H1: the first version of this decode kept only `sum` and
// `calculated` and read every other stored method as undefined, so an org's
// percent metric (stored `avg` since 20260910000050) fell through
// `resolveAggregationMethod` to its kind-based default, `sum`, and the
// editor offered a fold the RPC would never run. Every method in
// `IMPLEMENTED_AGGREGATION_METHODS` is now kept as stored; only a value
// outside that list reads as undefined, and `assertRegistryConsistent`
// then reports it by key (M3) instead of the page silently defaulting.

import {
  assertRegistryConsistent,
  IMPLEMENTED_AGGREGATION_METHODS,
  type PnlAggregationMethod,
  type PnlMetricDefinition,
  type PnlMetricRole,
  type PnlUnit,
} from "../../lib/pnl/pnlAnalytics";
import type { PnlMetricOption } from "./metricOption";

/** One row of `pnl_metric_registry` as PostgREST returns it. */
export interface RawRegistryRow {
  metric_key: string;
  organization_id: string | null;
  section: string;
  display_name: string;
  kind: string;
  unit: string;
  sort_order: number;
  is_active: boolean;
  is_deductible: boolean;
  metric_role: string | null;
  financial_inclusion: boolean | null;
  aggregation_method: string | null;
  decimals: number | null;
}

const SECTIONS = new Set(["revenue", "budget", "expense", "labour", "calculated"]);
const UNITS = new Set<PnlUnit>(["currency", "percent", "hours", "count", "ratio", "currency_per_hour"]);
const ROLES = new Set<PnlMetricRole>(["financial", "operational", "calculated"]);

function decodeAggregationMethod(raw: string | null): PnlAggregationMethod | undefined {
  if (raw === null) return undefined;
  return (IMPLEMENTED_AGGREGATION_METHODS as readonly string[]).includes(raw) ? (raw as PnlAggregationMethod) : undefined;
}

/** Decodes one row, or null when its section or kind is outside the vocabulary. */
export function decodeRegistryRow(row: RawRegistryRow): PnlMetricOption | null {
  if (!SECTIONS.has(row.section)) return null;
  if (row.kind !== "input" && row.kind !== "calculated") return null;
  const unit = UNITS.has(row.unit as PnlUnit) ? (row.unit as PnlUnit) : "currency";
  const role = ROLES.has(row.metric_role as PnlMetricRole) ? (row.metric_role as PnlMetricRole) : undefined;

  return {
    metricKey: row.metric_key,
    section: row.section as PnlMetricDefinition["section"],
    kind: row.kind,
    unit,
    metricRole: role,
    financialInclusion: row.financial_inclusion ?? undefined,
    aggregationMethod: decodeAggregationMethod(row.aggregation_method),
    decimals: row.decimals ?? undefined,
    deductible: row.is_deductible,
    displayName: row.display_name,
    sortOrder: row.sort_order,
    isCustom: row.organization_id !== null,
  };
}

export interface DecodedRegistry {
  options: PnlMetricOption[];
  /**
   * M3: the message from `assertRegistryConsistent` when the decoded table
   * violates a registry invariant (an unimplemented method, a ratio unit
   * tagged sum, a missing role). Null when the table is consistent. The
   * caller shows it as a page error and falls back to the built-in
   * vocabulary rather than crashing or silently defaulting.
   */
  error: string | null;
}

/** Decodes every row and checks the result against the engine's registry invariants. */
export function decodeRegistryRows(rows: RawRegistryRow[]): DecodedRegistry {
  const options = rows.map(decodeRegistryRow).filter((m): m is PnlMetricOption => m !== null);
  try {
    assertRegistryConsistent(options);
    return { options, error: null };
  } catch (err) {
    return { options, error: err instanceof Error ? err.message : String(err) };
  }
}
